import React, { Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { routesAtom } from '../../Atoms';
import { flatRoutesAtom } from '../routeAtoms';
import { resolveRouteKey, ROUTES_COMPONENTS } from '../routes';
import PageFallback from './PageFallback';

/**
 * Renders the page for the current location from the server route tree.
 *
 * Mounted once at `*` inside the authenticated layout instead of registering a
 * child route per feature. That keeps the router config independent of the
 * fetched tree, so the router is built once rather than rebuilt when the tree
 * arrives — rebuilding it required remounting RouterProvider, since it does not
 * adopt a replaced `router` prop.
 *
 * Route registration stays channel-agnostic on purpose: a long-channel-only
 * route must still be reachable by URL, exactly as when it was a real route.
 *
 * Pages are `lazy()` (see ROUTES_COMPONENTS), so this is also the app's single
 * Suspense boundary for page code. It sits inside MainDiv, below the sidebar and
 * navbar, so only the content area is affected while a chunk loads — the chrome
 * never blanks. `React.lazy` memoizes, so the fallback is a first-visit-per-page
 * cost, not a per-navigation one.
 */
const DynamicSection = function DynamicSection() {
  const { pathname } = useLocation();
  const routesTree = useAtomValue(routesAtom);
  const flatRoutes = useAtomValue(flatRoutesAtom);

  // Nothing may redirect before the tree arrives, or a reload on a feature path
  // would be bounced to '/' and lose the URL.
  if (!routesTree?.length) return null;

  const route = flatRoutes.find(({ target }) => `/${target}` === pathname);
  const Component = ROUTES_COMPONENTS[resolveRouteKey(route)];

  if (!Component) {
    // '*' also matches the index path, and no route has an empty target, so
    // redirecting to '/' from '/' would loop. AuthenticatedLayoutPage sends the
    // user on to their default page from there.
    return pathname === '/' ? null : <Navigate to="/" replace />;
  }

  // PageFallback draws nothing for its first 200ms, so a warm navigation still
  // shows no spinner while a cold one is not left staring at a blank panel.
  // Chunks are 8-10x larger in development than in production (unminified, plus
  // eval-source-map), so the wait this covers is mostly a dev-mode artifact.
  // Keyed by path: routes sharing one page (LIGHT, CORPORATE…) resolve to the
  // same component, and React would otherwise keep it mounted with the previous
  // route's state and data.
  return (
    <Suspense fallback={<PageFallback />}>
      <Component key={pathname} />
    </Suspense>
  );
};

export default DynamicSection;
